import React from 'react';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import moment from 'moment';
import * as _ from 'lodash';
import Immutable from 'seamless-immutable';
import * as invoiceActions from '../actions/invoice';
import PageNotFound from './PageNotFound';
import LoadingScreen from './LoadingScreen';
import PageHeader from './boxes/PageHeader';
import PageBody from './boxes/PageBody';
import MainContent from './boxes/MainContent';
import Modal from './boxes/Modal';
import NotificationBanner from './boxes/NotificationBanner';
import FreezeBanner from './boxes/FreezeBanner';
import PaymentFrequencySelector from './PaymentFrequencySelector';
import { range } from 'lodash';
import { useTranslation, withTranslation } from 'react-i18next';
import { appConfig } from '../config/appConfig';

class InvoiceDetailsComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            currentTab: 'details',
            addEmployee: false,
            renderEnrollees: false,
            openPaymentFrequencyModal: false,
            notification: null
        };
    }

    componentDidMount() {
        const { dispatch, user, match } = this.props;
        dispatch(
            invoiceActions.getClientInvoiceByRefCode(
                match.params.invoiceId,
                user.credentials.accessToken,
                user.companyInfo.id
            )
        );
    }

    onSwitchTab = () => {
        this.setState({
            currentTab: this.state.currentTab === 'details' ? 'payments' : 'details'
        });
    };

    togglePaymentFrequencyModal = () => {
        const isCurrentlyOpen = this.state.openPaymentFrequencyModal;
        // Only block opening the modal while frozen; never block closing it.
        if (!isCurrentlyOpen && this.props.selectedInvoice?.is_frozen) return;
        this.setState({ openPaymentFrequencyModal: !isCurrentlyOpen });
    };

    onFrequencyChanged = (err, message) => {
        const notification = {
            type: err ? 'Error' : 'Success',
            message: err ? err : message
        };
        this.setState({ notification });
        this.togglePaymentFrequencyModal();
    };

    renderLoading() {
        return (
            <MainContent>
                <PageBody>
                    <LoadingScreen />
                </PageBody>
            </MainContent>
        );
    }

    renderInvoiceNotFound() {
        return (
            <MainContent>
                <PageNotFound />
            </MainContent>
        );
    }

    goToEnrollees = () => {
        this.setState({ renderEnrollees: true });
    };

    renderEnrolleesPage = () => {
        return (
            <Redirect
                to={{
                    pathname: `/dashboard/staff`,
                    state: {
                        invoiceRefCode: this.props.selectedInvoice.reference_code
                    }
                }}
            />
        );
    };

    render() {
        const { currentTab, renderEnrollees, openPaymentFrequencyModal, notification } = this.state;
        const { isFetching, error, selectedInvoice, user, clientType, t } = this.props;
        const daysToCoverEnd = getDaysToCoverEnd(selectedInvoice.end_date);
        if (error && error.code === 404) return this.renderInvoiceNotFound();

        if (renderEnrollees) return this.renderEnrolleesPage();

        if (!isFetching && !_.isEmpty(selectedInvoice))
            return (
                <MainContent>
                    {openPaymentFrequencyModal && (
                        <FrequencyModal
                            onClose={this.togglePaymentFrequencyModal}
                            onFrequencyChanged={this.onFrequencyChanged}
                            invoice={selectedInvoice}
                            user={user}
                            selectedFrequency={getPaymentFrequency(
                                selectedInvoice.payment_frequency_id
                            ).toLowerCase()}
                        />
                    )}
                    <PageHeader
                        title={t("invoices.invoiceDetailsPageTitle")}
                        showButton
                        buttonTitle={t("invoices.viewEnrolleeButtonText")}
                        onButtonClick={this.goToEnrollees}
                    />
                    <PageBody>
                        {!!selectedInvoice.is_frozen && <FreezeBanner />}
                        {notification && (
                            <NotificationBanner
                                title={notification.type}
                                message={notification.message}
                                type={notification.type.toLowerCase()}
                            />
                        )}
                        <div className="dash__tab">
                            <button
                                className={`tablinks ${currentTab === 'details' ? 'activeTab' : ''}`}
                                onClick={this.onSwitchTab}
                            >
                                <h3 className="invoice-expanded__headertext">
                                    {t("invoices.invoicesTabSwitchButtonText", { selectedInvoiceReferenceCode: selectedInvoice.reference_code } )}
                                </h3>
                            </button>
                            <button
                                className={`tablinks ${currentTab === 'payments' ? 'activeTab' : ''}`}
                                onClick={this.onSwitchTab}
                            >
                                <h3 className="invoice-expanded__headertext">{t("invoices.paymentTabSwitchButtonText")}</h3>
                            </button>
                        </div>
                        <div className="invoice-expanded">
                            {currentTab === 'details' ? (
                                <DetailsView
                                    invoice={selectedInvoice}
                                    plans={selectedInvoice.products}
                                    showFrequencyModal={this.togglePaymentFrequencyModal}
                                    clientType={clientType}
                                />
                            ) : (
                                <PaymentsView payments={selectedInvoice.payments} />
                            )}
                        </div>
                    </PageBody>
                </MainContent>
            );
        return this.renderLoading();
    }
}

const DetailsView = ({ invoice, plans, showFrequencyModal, clientType }) => {
    const { t } = useTranslation();
    let amount_paid =
        invoice.payments &&
        invoice.payments.reduce(
            (totalAmountPaid, currentPayment) => +(totalAmountPaid + currentPayment.amount),
            0
        );
    return (
        <div className="invoice-expanded__table tabDashboard" style={{ display: 'flex' }}>
            <div className="dashboard_profile">
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        <div className="profile__info" style={{ paddingBottom: '0' }}>
                            <p className="profile__label">{t("invoices.invoiceDetailsDateIssued")}</p>
                            <p className="profile__infotext">
                                {t("common.date", { date: invoice.created_at })}
                            </p>
                            <p className="profile__label">{t("invoices.invoiceDetailsPaymentStatus")}</p>
                            <p className="profile__infotext">
                                {getInvoiceStatusIndicator(invoice.payment_status_id, t)}
                            </p>
                        </div>
                        <div className="profile__info" style={{ paddingBottom: '0' }}>
                            <p className="profile__label">{t("invoices.invoiceDetailsAmountPaid")}</p>
                            <p className="profile__infotext">
                                {t("common.amount", { amount: amount_paid })}
                            </p>
                            <p className="profile__label">{t("invoices.invoiceDetailsBalanceDue")}</p>
                            <p className="profile__infotext">
                                {t("common.amount", { amount: invoice.total_price })}
                            </p>
                        </div>
                        <div className="profile__info" style={{ paddingBottom: '0' }}>
                            <p className="profile__label">{t("invoices.invoiceDetailsCoverStartDate")}</p>
                            <p className="profile__infotext">
                                {t("common.date", { date: invoice.start_date })}
                            </p>
                            <p className="profile__label">{t("invoices.invoiceDetailsCoverEndDate")}</p>
                            <p className="profile__infotext">
                                {t("common.date", { date: invoice.end_date })}
                            </p>
                        </div>
                        <div
                            className="profile__info"
                            style={{ justifyContent: 'normal', paddingBottom: '0' }}
                        >
                            <p className="profile__label">{t("invoices.invoiceDetailsFrequency")}</p>
                            <p className="profile__infotext">
                                {t(`common.${getPaymentFrequency(invoice.payment_frequency_id).toLowerCase()}`)}
                            </p>

                            {clientType === 'SME' && (
                                <button
                                    className="primary_button"
                                    style={{ margin: 0 }}
                                    onClick={showFrequencyModal}
                                    disabled={!!invoice.is_frozen}
                                >
                                    {t("invoiceschangeFrequencyButtonText")}
                                </button>
                            )}
                        </div>
                    </div>
                    {invoice.new_frequency_id && (
                        <p style={{ padding: '20px', fontSize: '1.4em', color: 'grey' }}>
                            <span style={{ color: '#449802', textTransform: 'uppercase' }}>
                                {t(`common.${getPaymentFrequency(invoice.new_frequency_id)}`)}
                            </span>{' '}
                            {t("invoices.frequencyBeginsAtNextCycle")}
                        </p>
                    )}
                </div>
                <br />
                <br />
            </div>
            <div style={{ margin: '0 2rem', overflowX: 'scroll' }}>
                <PlansView plans={plans} />
            </div>
        </div>
    );
};

const PlansView = ({ plans }) => {
    const { t } = useTranslation();
    return (
        <table className="dashboard__table grey_bg">
            <thead>
                <tr>
                    <th>{t("invoices.planTablePlanDescriptionColumnHeader")}</th>
                    <th>{t("invoices.planTableEmployeesColumnHeader")}</th>
                    <th>{t("invoices.planTableAmountColumnHeader")} ({appConfig.countryCurrency})</th>
                    <th>{t("invoices.planTableEmployeesLeftColumnHeader")}</th>
                </tr>
            </thead>
            <tbody>
                {plans?.map((plan, index) => (
                    <tr key={index}>
                        <td>{plan.name}</td>
                        <td>{t("common.quantity", { quantity: plan.no_of_enrollees })}</td>
                        <td>{t("common.amount", { amount: plan.price })}</td>
                        <td>{t("common.quantity", { quantity: plan.enrollees_left })}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

const PaymentsView = ({ payments }) => {
    const { t } = useTranslation();
    return (
        <div className="invoice-expanded__payments tabDashboard" style={{ display: 'flex' }}>
            <table className="dashboard__table">
                <thead>
                    <tr>
                        <th>{t("invoices.paymentDetailsTableDatePaidColumnHeader")}</th>
                        <th>{t("invoices.paymentDetailsTableAmountColumnHeader")} ({appConfig.countryCurrency})</th>
                        <th>{t("invoices.paymentDetailsTableDescriptionColumnHeader")}</th>
                    </tr>
                </thead>
                <tbody>
                    {payments.map((payment, index) => (
                        <tr key={index}>
                            <td>{t("common.date", { 
                                    date: payment.created_at,
                                    formatParams: {
                                        date: {
                                            day: "2-digit",
                                            month: "2-digit",
                                            year: "numeric",
                                            hour: "numeric",
                                            minute: "numeric",
                                            second: "numeric",
                                            hour12: true
                                        },
                                    }
                                })}
                            </td>
                            <td>{t("common.amount", { amount: payment.amount })}</td>
                            <td>{payment.description || t("invoices.noDescription")}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const FrequencyModal = ({ selectedFrequency, onClose, invoice, user, onFrequencyChanged }) => {
    const { t } = useTranslation();
    return (
        <Modal open={true} onClose={onClose} title={t("invoices.changeFrequencyModalTitle")}>
            {/* show frequency options...along with their respective prices */}
            <PaymentFrequencySelector
                user={user}
                invoice={invoice}
                exclude={selectedFrequency}
                onFrequencyChanged={onFrequencyChanged}
            />
        </Modal>
    );
};

const getInvoiceStatusIndicator = (status, t) => {
    if (status === 1) {
        return t("invoices.noPayment");
    } else if (status === 2) {
        return t("invoices.partialPayment");
    } else {
        return t("invoices.paymentComplete");
    }
};

export const getPaymentFrequency = (id) => {
    switch (id) {
        case 1:
            return 'Daily';
        case 2:
            return 'Weekly';
        case 3:
            return 'Monthly';
        case 4:
            return 'Yearly';
        case 5:
            return 'Biennially';
        case 6:
            return 'Endlessly';
        case 7:
            return 'Quarterly';
        case 8:
            return 'Biannually';
        case 9:
            return 'Triannually';
        default:
            return 'Monthly';
    }
};

export const getDaysToCoverEnd = (coverEndDate) => {
    const today = moment();
    const specificDate = moment(coverEndDate, 'YYYY-MM-DD');
    const daysDifference = specificDate.diff(today, 'days');
    return daysDifference;
};

export const getCoverWarningType = (daysToCoverEnd) => {
    const warningLevel1 = range(-100, 15);
    const warningLevel2 = range(15, 31);
    const warningLevel3 = range(30, 46);

    if (warningLevel1.includes(daysToCoverEnd)) {
        return 'invoice_end_alert';
    }

    if (warningLevel2.includes(daysToCoverEnd)) {
        return 'invoice_end_notice';
    }

    if (warningLevel3.includes(daysToCoverEnd)) {
        return 'invoice_end_warning';
    }
};

const mapStateToProps = ({ invoice, session }) => ({
    isFetching: invoice.isFetching,
    selectedInvoice: invoice.selectedInvoice
        ? Immutable.asMutable(invoice.selectedInvoice, { deep: true })
        : {},
    error: invoice.error,
    clientType: session?.user?.companyInfo?.type
});

const InvoiceDetailsComponentwithTranslation = withTranslation()(InvoiceDetailsComponent);
export default withRouter(connect(mapStateToProps)(InvoiceDetailsComponentwithTranslation));

